import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import FormEmbed from "./form.tsx";
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  Database,
  FileText,
  Mail,
  Menu,
  Phone,
  X,
} from "lucide-react";

const SITE_URL = "https://herculesautomated.com";
const LOGO_URL = `${SITE_URL}/hercules_automation_logo.webp`;
const CONTACT_EMAIL = "contact@herculesautomated.com";
const CALENDLY_URL = "https://calendly.com/herculesautomation";

interface MousePos {
  x: number;
  y: number;
}

type RouteKey =
  | "/"
  | "/services"
  | "/case-studies"
  | "/about"
  | "/contact"
  | "/blog"
  | "/privacy-policy"
  | "/404.html";

const routes: Record<
  RouteKey,
  {
    title: string;
    description: string;
    primaryKeyword: string;
    heading: string;
  }
> = {
  "/": {
    title: "Hercules Automation | AI Automation Agency",
    description:
      "Hercules Automation is an AI automation agency helping small businesses automate workflows, admin, documents, CRM updates, follow-up, and customer communication fast.",
    primaryKeyword: "AI automation agency",
    heading: "AI-Powered Workflow Automation for Growing Businesses",
  },
  "/services": {
    title: "Business Process Automation | Hercules Automation",
    description:
      "Business process automation services for small businesses: AI admin automation, document workflows, scheduling, CRM sync, follow-up, and customer communication.",
    primaryKeyword: "business process automation",
    heading: "Business Process Automation Services",
  },
  "/case-studies": {
    title: "AI Workflow Automation Results | Hercules Automation",
    description:
      "See how AI workflow automation for small business saves time, captures leads, reduces admin costs, improves follow-up, and keeps daily operations moving.",
    primaryKeyword: "AI workflow automation for small business",
    heading: "AI Workflow Automation Results for Small Business",
  },
  "/about": {
    title: "Workflow Automation Services | Hercules Automation",
    description:
      "Hercules Automation delivers managed workflow automation services for owners and teams that need reliable AI systems, cleaner handoffs, and less admin.",
    primaryKeyword: "workflow automation services",
    heading: "Workflow Automation Services Built Around Your Operation",
  },
  "/contact": {
    title: "AI Admin Automation | Hercules Automation",
    description:
      "Book a discovery call for AI admin automation that eliminates repetitive tasks in scheduling, inboxes, documents, CRM updates, follow-up, and customer communication.",
    primaryKeyword: "AI admin automation",
    heading: "Start Automating Repetitive Admin Work",
  },
  "/blog": {
    title: "Save Time With AI Automation | Hercules Automation",
    description:
      "Learn how to automate client onboarding, choose AI tools for admin tasks, and save time with practical AI workflow automation guides for small businesses.",
    primaryKeyword: "save time with AI automation",
    heading: "Practical Guides to Save Time With AI Automation",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Hercules Automation",
    description:
      "Read the Hercules Automation privacy policy for website visitors, leads, and clients using AI workflow automation, intake, scheduling, and communication services.",
    primaryKeyword: "privacy policy",
    heading: "Privacy Policy",
  },
  "/404.html": {
    title: "404 Page Not Found | Hercules Automation",
    description:
      "The requested Hercules Automation page was not found. Visit the homepage for AI automation agency services, guides, case studies, and contact options.",
    primaryKeyword: "AI automation agency",
    heading: "404 Page Not Found",
  },
};

const navLinks = [
  { href: "/services", label: "Services" },
  { href: "/case-studies", label: "Results" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Guides" },
  { href: "/contact", label: "Contact" },
];

const services = [
  {
    name: "Document Systems",
    icon: FileText,
    description:
      "Read, classify, route, and file incoming PDFs, forms, contracts, and intake documents automatically.",
  },
  {
    name: "Data Entry and CRM Sync",
    icon: Database,
    description:
      "Pull data from emails, forms, spreadsheets, and documents into your CRM with fewer manual handoffs.",
  },
  {
    name: "Inbox and Comms Management",
    icon: Mail,
    description:
      "Categorize messages, draft replies, surface priority work, and keep customer follow-up moving.",
  },
  {
    name: "Scheduling and Booking",
    icon: Calendar,
    description:
      "Confirm appointments, send reminders, handle rescheduling, and update calendars around the clock.",
  },
  {
    name: "Inbound Call Systems",
    icon: Phone,
    description:
      "Answer calls, qualify leads, capture details, and book appointments without missed opportunities.",
  },
];

const caseStudies = [
  {
    label: "Case Study 01",
    title: "A local gym stopped losing leads with an automated front desk.",
    outcomes: [
      ["100%", "Calls answered"],
      ["50", "Leads captured per month"],
      ["20%", "Conversion to booked appointments"],
      ["88%", "Cost reduction vs. hiring"],
    ],
    body: [
      "During peak hours, inbound calls went unanswered. Prospective members had questions about pricing, classes, hours, and availability, but staff were often busy helping people in the gym.",
      "Hercules built an automated front desk that answers calls, responds to common questions, captures lead details, and stores each qualified prospect in the CRM for follow-up.",
    ],
    callout: "Every call is now handled consistently, and new leads are captured without pulling staff away from members.",
    saving: "$2,200 per month in savings vs. part-time front-desk staffing.",
  },
];

const faqItems = [
  {
    question: "How can AI automate client onboarding?",
    answer:
      "AI can collect intake details, read uploaded documents, create CRM records, assign tasks, send welcome emails, and schedule kickoff calls from one repeatable workflow.",
  },
  {
    question: "What are the best AI tools to automate admin tasks?",
    answer:
      "The best stack depends on your current systems, but common building blocks include AI intake forms, workflow automation platforms, CRM integrations, document parsers, and managed inbox or call agents.",
  },
  {
    question: "How quickly can a small business save time with AI automation?",
    answer:
      "Most high-volume admin workflows can be scoped in a discovery call and launched in days or weeks when the process is repeatable and the source systems are accessible.",
  },
];

function getRouteKey(): RouteKey {
  if (typeof window === "undefined") return "/";
  const pathname = window.location.pathname.replace(/\/$/, "") || "/";
  return pathname in routes ? (pathname as RouteKey) : "/";
}

function Seo({ route }: { route: RouteKey }) {
  const meta = routes[route];
  const canonical = `${SITE_URL}${route === "/" ? "/" : route}`;
  const serviceSchema = services.map((service) => ({
    "@type": "Service",
    name: service.name,
    serviceType: service.name,
    provider: { "@type": "Organization", name: "Hercules Automation", url: SITE_URL },
    areaServed: "United States",
    description: service.description,
    url: `${SITE_URL}/services`,
  }));
  const schema: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Hercules Automation",
      url: SITE_URL,
      logo: LOGO_URL,
      email: CONTACT_EMAIL,
      description:
        "AI automation agency helping small businesses automate workflow, admin, documents, scheduling, customer communication, and follow-up.",
      contactPoint: {
        "@type": "ContactPoint",
        email: CONTACT_EMAIL,
        contactType: "sales",
        areaServed: "US",
        availableLanguage: "English",
      },
      sameAs: [CALENDLY_URL],
    },
    ...serviceSchema,
  ];

  if (route !== "/") {
    schema.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: meta.heading, item: canonical },
      ],
    });
  }

  if (route === "/" || route === "/blog") {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });
  }

  return (
    <Helmet>
      <html lang="en" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={route === "/404.html" ? "noindex, follow" : "index, follow"} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Hercules Automation" />
      <meta property="og:image" content={LOGO_URL} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={LOGO_URL} />
      <link rel="preload" as="image" href="/hercules_mark.webp" />
      <script id="structured-data" type="application/ld+json">
        {JSON.stringify(schema).replace(/</g, "\\u003c")}
      </script>
    </Helmet>
  );
}

function HeroCanvas({ mouse }: { mouse: React.RefObject<MousePos> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    interface Pt {
      x: number;
      y: number;
      vx: number;
      vy: number;
    }

    let pts: Pt[] = [];
    let animId = 0;
    let width = 0;
    let height = 0;

    const init = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = parent.offsetWidth;
      height = parent.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      const count = Math.max(36, Math.floor((width * height) / 24000));
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      const mx = mouse.current.x;
      const my = mouse.current.y;
      const hasMouse = mx > -100;

      for (const p of pts) {
        if (hasMouse) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > 0 && distance < 180) {
            const force = ((180 - distance) / 180) * 0.012;
            p.vx += (dx / distance) * force;
            p.vy += (dy / distance) * force;
          }
        }
        p.vx *= 0.972;
        p.vy *= 0.972;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x += width;
        if (p.x > width) p.x -= width;
        if (p.y < 0) p.y += height;
        if (p.y > height) p.y -= height;
      }

      for (let i = 0; i < pts.length; i += 1) {
        for (let j = i + 1; j < pts.length; j += 1) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 140) {
            ctx.strokeStyle = `rgba(13,31,60,${(1 - distance / 140) * 0.13})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }

      for (const p of pts) {
        const dx = mx - p.x;
        const dy = my - p.y;
        const distance = hasMouse ? Math.sqrt(dx * dx + dy * dy) : 999;
        const near = distance < 180;
        const t = near ? 1 - distance / 180 : 0;
        ctx.fillStyle = near
          ? `rgba(201,162,77,${0.45 + t * 0.45})`
          : "rgba(13,31,60,0.20)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2 + t * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(tick);
    };

    init();
    tick();
    const resizeObserver = new ResizeObserver(init);
    if (canvas.parentElement) resizeObserver.observe(canvas.parentElement);

    return () => {
      cancelAnimationFrame(animId);
      resizeObserver.disconnect();
    };
  }, [mouse]);

  return <canvas ref={canvasRef} aria-hidden="true" className="hero-canvas" />;
}

function Header({ route }: { route: RouteKey }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header>
      <nav className={`nav${scrolled ? " nav-scrolled" : ""}`} aria-label="Primary navigation">
        <div className="nav-container">
          <a href="/" className="logo-wrapper" onClick={closeMobile} aria-label="Hercules Automation home">
            <span className="logo-mark" aria-hidden="true">
              <img
                src="/hercules_mark.webp"
                alt=""
                className="logo-img"
                width="598"
                height="681"
                draggable={false}
              />
            </span>
            <span className="logo-text">
              <span>Hercules&nbsp;</span>
              <span className="gold-part">Automation</span>
            </span>
          </a>

          <div id="primary-navigation" className={`nav-links${mobileMenuOpen ? " nav-open" : ""}`}>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={closeMobile}
                aria-current={route === link.href ? "page" : undefined}
              >
                {link.label}
              </a>
            ))}
            <div className="nav-ctas">
              <a href={`mailto:${CONTACT_EMAIL}`} className="btn-ghost-nav">
                Email Us
              </a>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
                Book a Call
              </a>
            </div>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-navigation"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
    </header>
  );
}

function Hero() {
  const heroMouse = useRef<MousePos>({ x: -9999, y: -9999 });

  return (
    <section
      className="hero"
      aria-labelledby="home-heading"
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        heroMouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      }}
      onMouseLeave={() => {
        heroMouse.current = { x: -9999, y: -9999 };
      }}
    >
      <HeroCanvas mouse={heroMouse} />
      <div className="hero-inner">
        <div className="hero-content">
          <p className="hero-label">AI Automation Agency</p>
          <h1 id="home-heading">We Automate the Work Your Team Hates Doing</h1>
          <p className="hero-sub">
            Most businesses lose 15-20 hours a week to tasks AI can handle in seconds. We find those tasks, build the
            automations, and hand you back your time — calls, scheduling, follow-ups, and CRM updates all handled
            automatically.
          </p>
          <div className="hero-actions">
            <a href="/contact" className="btn-primary btn-lg">
              Get Your Free Automation Audit
            </a>
            <a href="/services" className="btn-outline btn-lg">
              See Our Work
            </a>
          </div>
          <p className="hero-note">30 minutes. No commitment. Honest assessment.</p>
        </div>

        <aside className="hero-panel" aria-label="Manual admin cost statistics">
          <p className="hero-panel-label">The cost of manual work</p>
          <div className="hero-panel-stat">
            <span className="hps-num">36%</span>
            <span className="hps-text">of your workweek is eaten by admin — before real work begins</span>
          </div>
          <div className="hero-panel-divider" />
          <div className="hero-panel-stat">
            <span className="hps-num">40%</span>
            <span className="hps-text">of productivity vanishes from manual handoffs and tool-switching</span>
          </div>
          <div className="hero-panel-divider" />
          <div className="hero-panel-stat">
            <span className="hps-num">80%</span>
            <span className="hps-text">of your repetitive admin can be fully automated — starting this week</span>
          </div>
          <div className="hero-panel-divider" />
          <div className="hero-panel-stat">
            <span className="hps-num">10x</span>
            <span className="hps-text">faster response times when follow-ups and outreach run automatically</span>
          </div>
        </aside>
      </div>
    </section>
  );
}

function SolutionSection() {
  return (
    <section className="solution-section" aria-labelledby="solution-heading">
      <div className="solution-inner">
        <div className="solution-header">
          <p className="section-label">The Solution</p>
          <h2 id="solution-heading" className="section-h2">
            Put business process automation on rails.
          </h2>
          <p className="solution-lead">
            Hercules designs and manages AI business systems for the repeatable tasks your team handles every day.
            AI reads, routes, responds, updates records, and keeps work moving without constant manual effort.
          </p>
        </div>

        <div className="solution-cards-row">
          {["Runs 24/7", "Live in days, not months", "Pays for itself", "Fully managed by us"].map((title, index) => (
            <article className="solution-feature" key={title}>
              <CheckCircle size={18} className="sf-icon" aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>
                  {index === 0 && "Across forms, emails, scheduling, calls, and back-office workflows with fewer gaps."}
                  {index === 1 && "Most initial automations can be scoped, built, and launched quickly."}
                  {index === 2 && "Automate repetitive tasks before adding more admin headcount."}
                  {index === 3 && "We build, monitor, and maintain the workflows after launch."}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="before-after">
          <article className="before-card">
            <p className="ba-label">Before</p>
            <p className="ba-body">
              Your team juggles phones, paperwork, follow-up, and data entry between customer work. Important details
              slip through the cracks.
            </p>
          </article>
          <div className="ba-arrow-wrap" aria-hidden="true">
            <ArrowRight size={24} className="ba-arrow-icon" />
          </div>
          <article className="after-card">
            <p className="ba-label">After Hercules</p>
            <p className="ba-body">
              Repeatable work runs in the background. Your people focus on customers and revenue while the system
              shows exactly what happened.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function ServicesSection({ standalone = false }: { standalone?: boolean }) {
  return (
    <section className="services-section" aria-labelledby="services-heading">
      <div className="container">
        <div className="services-header">
          <div className="services-header-left">
            <p className="section-label">What We Build</p>
            {standalone ? (
              <h1 id="services-heading" className="page-h1">
                Business Process Automation Services
              </h1>
            ) : (
              <h2 id="services-heading" className="section-h2">
                Workflow automation services built around how your business runs.
              </h2>
            )}
            <p className="services-intro">
              We connect documents, emails, scheduling, calls, and data into managed systems that automate repetitive
              tasks and reduce day-to-day admin load.
            </p>
          </div>
        </div>

        <ul className="services-list">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <li className="service-item" key={service.name}>
                <span className="service-num">{String(index + 1).padStart(2, "0")}</span>
                <div className="service-icon-wrap" aria-hidden="true">
                  <Icon size={20} />
                </div>
                <div className="service-text">
                  <h3>{service.name}</h3>
                  <p>{service.description}</p>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="services-footnote">
          If a process follows rules, repeats often, and costs your team time, it is a candidate for AI workflow
          automation.
        </p>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    ["Discover", "We map the current workflow and identify the highest-impact place to start."],
    ["Build", "We design the automation around your tools, handoffs, language, rules, and exceptions."],
    ["Launch", "We go live with monitoring, review outputs, and tune the system against real work."],
    ["Expand", "Once the first workflow is stable, we layer in the next process with the same operating model."],
  ];

  return (
    <section className="how-section" aria-labelledby="how-heading">
      <div className="how-inner">
        <div className="how-header-col">
          <p className="section-label">How It Works</p>
          <h2 id="how-heading" className="section-h2">
            From discovery to live in days.
          </h2>
          <p className="how-sub">
            A structured process gets your first automation running quickly, then keeps it improving.
          </p>
          <p className="how-callout">Most clients are live within 2 weeks when source systems are accessible.</p>
        </div>

        <div className="steps-col">
          {steps.map(([title, body], index) => (
            <article className={`step-item${index === steps.length - 1 ? " step-item-last" : ""}`} key={title}>
              <div className="step-left">
                <div className="step-badge">{String(index + 1).padStart(2, "0")}</div>
              </div>
              <div className="step-right">
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ResultsSection({ standalone = false }: { standalone?: boolean }) {
  return (
    <section className="results-section" aria-labelledby="results-heading">
      <div className="container">
        <div className="results-header">
          <p className="section-label-light">Client Results</p>
          {standalone ? (
            <h1 id="results-heading" className="page-h1 page-h1-light">
              Client Results
            </h1>
          ) : (
            <h2 id="results-heading" className="section-h2-light">
              Client results from real automation systems.
            </h2>
          )}
        </div>

        <div className="case-studies-list">
          {caseStudies.map((study) => (
            <article className="case-study-card" key={study.title}>
              <div className="cs-content">
                <p className="cs-label">{study.label}</p>
                <h2 className="cs-title">{study.title}</h2>
                <div className="cs-outcomes" aria-label={`${study.label} outcomes`}>
                  {study.outcomes.map(([num, label]) => (
                    <div className="cs-outcome-item" key={label}>
                      <span className="cs-outcome-num">{num}</span>
                      <span className="cs-outcome-label">{label}</span>
                    </div>
                  ))}
                </div>
                {study.body.map((paragraph) => (
                  <p className="cs-body" key={paragraph}>
                    {paragraph}
                  </p>
                ))}
                <div className="cs-callout">
                  <span>{study.callout}</span>
                </div>
                <p className="cs-saving">{study.saving}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="faq-section" aria-labelledby="faq-heading">
      <div className="container">
        <p className="section-label">FAQ</p>
        <h2 id="faq-heading" className="section-h2">
          Questions owners ask before automating admin.
        </h2>
        <div className="faq-list">
          {faqItems.map((item) => (
            <article className="faq-item" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogSection({ standalone = false }: { standalone?: boolean }) {
  return (
    <section className="blog-section" aria-labelledby="blog-heading">
      <div className="container">
        <p className="section-label">Guides</p>
        {standalone ? (
          <h1 id="blog-heading" className="page-h1">
            Practical Guides to Save Time With AI Automation
          </h1>
        ) : (
          <h2 id="blog-heading" className="section-h2">
            Learn where AI automation pays off first.
          </h2>
        )}
        <div className="blog-grid">
          {[
            [
              "How to automate client onboarding",
              "Turn intake forms, document collection, CRM setup, welcome emails, and kickoff scheduling into one reliable workflow.",
            ],
            [
              "Best AI tools to automate admin tasks",
              "A practical way to choose between document parsers, inbox agents, CRM automations, and managed workflow tools.",
            ],
            [
              "Save time with AI automation",
              "Find the repetitive tasks that drain your team and estimate which workflow should be automated first.",
            ],
          ].map(([title, body]) => (
            <article className="blog-card" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
              <a href="/contact">Discuss this automation</a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection({ standalone = false }: { standalone?: boolean }) {
  return (
    <section className="about-section" aria-labelledby="about-heading">
      <div className="container about-grid">
        <div>
          <p className="section-label">About Hercules</p>
          {standalone ? (
            <h1 id="about-heading" className="page-h1">
              Workflow Automation Services Built Around Your Operation
            </h1>
          ) : (
            <h2 id="about-heading" className="section-h2">
              Managed AI systems, not one-off zaps.
            </h2>
          )}
        </div>
        <div className="about-copy">
          <p>
            Hercules Automation is an AI automation agency for small-to-mid size business owners and operations
            managers who need practical systems, not more software to manage.
          </p>
          <p>
            We start with the process, build around your existing tools, and stay involved after launch so the workflow
            keeps working as your business changes.
          </p>
          <a href="/services" className="text-link">
            Review our business process automation services
          </a>
        </div>
      </div>
    </section>
  );
}

function ContactSection({ standalone = false }: { standalone?: boolean }) {
  return (
    <section className="contact-section" aria-labelledby="contact-heading">
      <div className="container">
        <div className="contact-header">
          <p className="section-label">Get Started</p>
          {standalone ? (
            <h1 id="contact-heading" className="page-h1">
              Start Automating Repetitive Admin Work
            </h1>
          ) : (
            <h2 id="contact-heading" className="cta-h2">
              Ready to take admin off your plate?
            </h2>
          )}
          <p className="cta-body">
            The first 30 minutes is a free discovery call. We map your processes and give you an honest assessment of
            whether a managed AI admin automation system makes sense for your business.
          </p>
          <p className="cta-note">Live in under 2 weeks if it is a fit.</p>
        </div>

        <div className="contact-options">
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-lg">
            Schedule on Calendly
          </a>
          <span className="contact-divider">or fill out the form below</span>
        </div>

        <div className="form-wrapper">
          <FormEmbed />
        </div>

        <p className="contact-alt">
          Prefer email? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </div>
    </section>
  );
}

function PrivacyPolicySection() {
  const privacyEmail = "privacy@herculesautomated.com";
  const policySections: Array<{
    title: string;
    paragraphs?: string[];
    afterItems?: string[];
    subsections?: Array<{
      title: string;
      paragraphs?: string[];
      afterItems?: string[];
      items?: string[];
    }>;
    items?: string[];
  }> = [
    {
      title: "1. About This Policy",
      paragraphs: [
        "Hercules Automation LLC (“Hercules Automation,” “we,” “us,” or “our”) provides AI-powered voice agents, workflow automation, document processing, CRM integration, scheduling systems, inbox management, and related automation services to small and mid-size businesses. Our registered place of business is in New York, NY.",
        "This Privacy Policy explains how we collect, use, disclose, and safeguard personal information when you visit our website (herculesautomated.com), contact us, or engage us to provide services. It also explains the rights you may have regarding your personal information.",
        "If you are a business client, this Policy governs information about your representatives and contacts. The handling of your customers’ data processed through the automations we build for you is governed separately by the Data Processing Agreement (“DPA”) or Master Services Agreement (“MSA”) between us.",
      ],
    },
    {
      title: "2. Contact Information",
      paragraphs: [
        "For any questions, requests, or concerns about this Policy or our privacy practices, please contact:",
        "Hercules Automation LLC",
        "Email: privacy@herculesautomated.com",
        "Website: herculesautomated.com/contact",
        "Location: New York, NY, United States",
        "We will respond to all verifiable requests within 30 days (GDPR) or 45 days (CCPA), with one possible 45-day extension where reasonably necessary.",
      ],
    },
    {
      title: "3. Information We Collect",
      subsections: [
        {
          title: "3a. Information You Provide Directly",
          paragraphs: [
            "When you contact us, submit a form, book a discovery call, or engage our services, we may collect:",
          ],
          items: [
            "Identity information: name, business name, job title",
            "Contact information: email address, phone number, mailing address",
            "Scheduling information: calendar availability, meeting preferences",
            "Project and scoping details: workflow descriptions, goals, pain points",
            "Files and documents: samples, templates, or examples you share for scoping or delivery",
            "Billing and payment details: invoicing information (we do not store payment card numbers)",
          ],
        },
        {
          title: "3b. Website and Usage Data",
          paragraphs: ["When you visit our website, we and our service providers may automatically collect:"],
          items: [
            "Technical data: IP address, device type, operating system, browser type and version",
            "Usage data: pages visited, time on page, referring URL, links clicked, form interactions",
            "Cookie and tracking data: see Section 7 (Cookies) below",
          ],
        },
        {
          title: "3c. Call and Voice Data",
          paragraphs: [
            "If you or your business contacts interact with an AI voice agent we operate or have deployed, we may collect:",
          ],
          items: [
            "Call metadata: caller ID, call duration, time and date, call outcome",
            "Call recordings and transcripts: subject to applicable consent and disclosure requirements (see Section 9)",
            "Voice inputs: audio processed by our AI voice platform to generate responses",
          ],
        },
        {
          title: "3d. Client Workflow Data",
          paragraphs: [
            "When we build or operate automations on your behalf, we may process business records, CRM data, customer communications, scheduling entries, documents, and other operational data you or your systems make available to us. We act as a data processor for this information; your privacy obligations to your own customers remain your responsibility and are addressed in your DPA with us.",
          ],
        },
      ],
    },
    {
      title: "4. How We Use Information and Our Legal Basis",
      paragraphs: [
        "We use personal information for the following purposes. Where GDPR applies, we identify the lawful basis for each:",
      ],
      items: [
        "Responding to inquiries and scheduling calls — Lawful basis: Legitimate interests / Pre-contractual steps",
        "Evaluating automation opportunities and preparing proposals — Lawful basis: Pre-contractual steps",
        "Providing, testing, monitoring, and improving contracted automation services — Lawful basis: Performance of contract",
        "Sending invoices and managing billing — Lawful basis: Performance of contract / Legal obligation",
        "Sending relevant service and business communications — Lawful basis: Legitimate interests",
        "Sending marketing emails to prospects (with opt-out) — Lawful basis: Legitimate interests (or Consent where required)",
        "Maintaining records and complying with legal obligations — Lawful basis: Legal obligation",
        "Operating, securing, and improving our website — Lawful basis: Legitimate interests",
        "Preventing fraud, abuse, or misuse of our systems — Lawful basis: Legitimate interests / Legal obligation",
      ],
      subsections: [
        {
          title: "",
          paragraphs: [
            "We do not use personal information for automated decision-making that produces legal or similarly significant effects without human oversight. If an automation we build for a client incorporates such decisions, this will be disclosed in the applicable SOW and DPA.",
          ],
        },
      ],
    },
    {
      title: "5. How We Share Information",
      paragraphs: [
        "We do not sell your personal information. We do not share personal information for cross-context behavioral advertising.",
        "We may share information in the following limited circumstances:",
      ],
      subsections: [
        {
          title: "5a. Service Providers and Sub-processors",
          paragraphs: [
            "We use trusted third-party platforms to deliver our services. These providers process data on our behalf and are contractually required to protect it. Categories include:",
          ],
          items: [
            "Cloud hosting and infrastructure",
            "Workflow automation platforms (e.g., n8n)",
            "AI voice and telephony platforms (e.g., Retell AI)",
            "AI language model providers",
            "CRM and business management software",
            "Email delivery and marketing platforms (e.g., Brevo)",
            "Scheduling and calendar tools",
            "Analytics and website performance tools",
            "Accounting and invoicing platforms",
          ],
          afterItems: [
            "Each provider operates under its own privacy and security terms. Upon request, we can provide a current list of key sub-processors.",
          ],
        },
        {
          title: "5b. Legal Requirements and Protection",
          paragraphs: [
            "We may disclose information if required by law, regulation, court order, or governmental authority, or where necessary to protect the rights, property, or safety of Hercules Automation, our clients, or others.",
          ],
        },
        {
          title: "5c. Business Transfers",
          paragraphs: [
            "If Hercules Automation is involved in a merger, acquisition, financing, or sale of assets, personal information may be transferred as part of that transaction. We will provide notice before personal information is transferred and becomes subject to a different privacy policy.",
          ],
        },
      ],
    },
    {
      title: "6. International Data Transfers",
      paragraphs: [
        "Hercules Automation is based in the United States. If you are located outside the US, please be aware that information you provide will be transferred to and processed in the United States, which may have data protection laws different from those in your country.",
        "When we transfer personal data originating from the European Economic Area (EEA), United Kingdom, or Switzerland to third countries, we rely on appropriate safeguards, which may include Standard Contractual Clauses (SCCs) approved by the European Commission or UK equivalent mechanisms. You may request a copy of applicable safeguards by contacting us at privacy@herculesautomated.com.",
      ],
    },
    {
      title: "7. Cookies and Similar Technologies",
      paragraphs: ["Our website uses cookies, pixels, and similar tracking technologies for the following purposes:"],
      items: [
        "Strictly necessary: keeping the site functioning, remembering session state",
        "Analytics and performance: understanding how visitors interact with our site (e.g., pages visited, traffic sources)",
        "Marketing: measuring the effectiveness of outreach and improving our messaging",
      ],
      subsections: [
        {
          title: "",
          paragraphs: [
            "Non-essential cookies (analytics, marketing) are placed only with your consent where required by applicable law. A cookie consent banner on our website allows you to accept or reject non-essential cookies.",
            "You may also control cookies through your browser settings. Disabling certain cookies may affect the functionality of parts of our website. For more information, visit allaboutcookies.org.",
          ],
        },
      ],
    },
    {
      title: "8. Data Retention",
      paragraphs: [
        "We retain personal information only as long as necessary for the purposes described in this Policy. Our general retention guidelines are:",
      ],
      items: [
        "Prospect and inquiry data: up to 24 months from last contact, then deleted or anonymized",
        "Active client data: for the duration of the engagement plus 3 years for accounting and legal purposes",
        "Call recordings and transcripts: 90 days, unless a longer period is required by law or agreed in writing",
        "Website analytics data: up to 26 months (per standard analytics platform settings)",
        "Financial and billing records: 7 years as required by US tax and accounting laws",
        "Legal hold data: retained as required by applicable legal proceedings",
      ],
      subsections: [
        {
          title: "",
          paragraphs: [
            "When retention periods expire, we delete or de-identify personal information using reasonable technical measures.",
          ],
        },
      ],
    },
    {
      title: "9. Call Recording and AI Voice Agent Disclosures",
      paragraphs: [
        "Hercules Automation deploys AI-powered voice agents for inbound call handling on behalf of clients. The following disclosures apply:",
      ],
      subsections: [
        {
          title: "Recording Consent",
          paragraphs: [
            "Calls handled by AI voice agents may be recorded and transcribed for quality assurance, training, and service delivery purposes. Callers are informed at the start of a call that they are speaking with an automated system and that the call may be recorded. Continued participation constitutes consent to recording under applicable one-party and two-party consent laws.",
          ],
        },
        {
          title: "State-Specific Compliance",
          paragraphs: [
            "Call recording laws vary by state. In two-party (all-party) consent states — including California, Florida, Illinois, Maryland, Massachusetts, Michigan, Montana, Nevada, New Hampshire, Oregon, Pennsylvania, and Washington — all parties must consent to recording. Our voice agent scripts are configured to obtain affirmative consent in these states before recording begins. Clients are responsible for informing us of the jurisdictions in which their callers are located so we can configure disclosures appropriately.",
          ],
        },
        {
          title: "TCPA Compliance",
          paragraphs: [
            "Our inbound voice agents respond to calls initiated by end users and do not place unsolicited outbound calls using auto-dialers. If outbound calling campaigns are scoped as an add-on service, a separate TCPA compliance addendum will govern that engagement.",
          ],
        },
        {
          title: "AI Disclosure",
          paragraphs: [
            "Callers are informed at the beginning of each interaction that they are communicating with an automated AI system, not a human agent. This disclosure is provided clearly and before any personal information is requested.",
          ],
        },
      ],
    },
    {
      title: "10. Data Security",
      paragraphs: [
        "We implement reasonable administrative, technical, and organizational security measures designed to protect personal information from unauthorized access, disclosure, loss, misuse, alteration, or destruction. These measures include encryption in transit, access controls, vendor security assessments, and periodic review of our practices.",
        "No method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.",
      ],
      subsections: [
        {
          title: "Data Breach Notification",
          paragraphs: [
            "In the event of a data breach that affects personal information and triggers notification obligations under applicable law, we will notify affected individuals and relevant supervisory authorities within the timeframes required by law (e.g., 72 hours for GDPR supervisory authority notification; applicable US state law timeframes for individual notification).",
          ],
        },
      ],
    },
    {
      title: "11. Your Privacy Rights",
      subsections: [
        {
          title: "All Users",
          paragraphs: ["Regardless of your location, you may:"],
          items: [
            "Request access to personal information we hold about you",
            "Request correction of inaccurate personal information",
            "Request deletion of personal information, subject to legal or contractual retention requirements",
            "Unsubscribe from marketing emails via the unsubscribe link in any email or by contacting us",
          ],
        },
        {
          title: "California Residents (CCPA / CPRA)",
          paragraphs: ["California residents have the following additional rights:"],
          items: [
            "Right to Know: request details about the categories and specific pieces of personal information we have collected about you",
            "Right to Delete: request deletion of personal information we collected from you",
            "Right to Correct: request correction of inaccurate personal information",
            "Right to Opt-Out: we do not sell personal information or share it for cross-context behavioral advertising, so no opt-out is currently required; however, you may direct any related request to us",
            "Right to Limit Use of Sensitive Personal Information: to the extent we collect sensitive personal information (as defined by CPRA), you may request we limit its use to purposes permitted by law",
            "Right to Non-Discrimination: we will not discriminate against you for exercising any CCPA/CPRA rights",
          ],
          afterItems: [
            "To submit a California privacy rights request, contact us at privacy@herculesautomated.com. We will verify your identity before processing your request and respond within 45 days (with one possible 45-day extension).",
          ],
        },
        {
          title: "EEA, UK, and Swiss Residents (GDPR / UK GDPR)",
          paragraphs: [
            "If you are located in the European Economic Area, United Kingdom, or Switzerland, you have the following rights under applicable data protection law:",
          ],
          items: [
            "Right of access (Article 15 GDPR)",
            "Right to rectification (Article 16 GDPR)",
            "Right to erasure / right to be forgotten (Article 17 GDPR)",
            "Right to restriction of processing (Article 18 GDPR)",
            "Right to data portability (Article 20 GDPR)",
            "Right to object to processing based on legitimate interests (Article 21 GDPR)",
            "Right to withdraw consent at any time, where processing is based on consent",
            "Right to lodge a complaint with your local supervisory authority (e.g., ICO in the UK, or your EU member state’s data protection authority)",
          ],
          afterItems: [
            "To exercise any of these rights, contact us at privacy@herculesautomated.com. We will respond within 30 days, with a possible 60-day extension for complex requests.",
          ],
        },
      ],
    },
    {
      title: "12. Business Client Data Processing",
      paragraphs: [
        "When Hercules Automation processes personal data on behalf of a business client in the course of delivering automation services, we act as a data processor (under GDPR) or service provider (under CCPA). The client is the data controller / business.",
        "Our data processing activities in this context are governed by the Data Processing Agreement (DPA) executed between us and the client, which includes:",
      ],
      items: [
        "Instructions for processing and permitted purposes",
        "Technical and organizational security measures",
        "Sub-processor disclosures and restrictions",
        "Data subject rights assistance obligations",
        "Breach notification procedures",
        "Data deletion or return upon termination",
      ],
      subsections: [
        {
          title: "",
          paragraphs: [
            "Business clients are responsible for ensuring they have a lawful basis for sharing data with us and for complying with applicable privacy laws with respect to their own customers.",
          ],
        },
      ],
    },
    {
      title: "13. Healthcare and HIPAA",
      paragraphs: [
        "If Hercules Automation is engaged by a healthcare provider, medical clinic, or med spa client that requires handling of Protected Health Information (PHI) as defined under the Health Insurance Portability and Accountability Act (HIPAA), we will execute a Business Associate Agreement (BAA) with that client prior to processing any PHI. We offer HIPAA compliance configurations as a designated add-on service. This general Privacy Policy does not govern PHI; the BAA and applicable HIPAA rules govern that processing.",
      ],
    },
    {
      title: "14. Children’s Privacy",
      paragraphs: [
        "Our website and services are directed to businesses and their professional representatives. We do not knowingly collect personal information from any individual under the age of 18. If you believe we have inadvertently received information from a minor, please contact us at privacy@herculesautomated.com and we will delete it promptly.",
      ],
    },
    {
      title: "15. Third-Party Links and Integrations",
      paragraphs: [
        "Our website and automations may contain links to or integrations with third-party platforms (e.g., booking tools, CRM systems, payment processors). This Privacy Policy does not apply to those third-party services. We encourage you to review the privacy policies of any third-party services you access through our website or automations.",
      ],
    },
    {
      title: "16. Do Not Sell or Share My Personal Information",
      paragraphs: [
        "Hercules Automation does not sell personal information to third parties. We do not share personal information for cross-context behavioral advertising as defined under the California Privacy Rights Act (CPRA). If you have questions about this or wish to confirm your data has not been shared, contact us at privacy@herculesautomated.com.",
      ],
    },
    {
      title: "17. Changes to This Policy",
      paragraphs: [
        "We may update this Privacy Policy from time to time as our services, technology, vendors, or legal obligations evolve. When we make material changes, we will update the “Last Revised” date at the top of this Policy and, where required by law or where we consider it appropriate, notify you by email or by a notice on our website.",
        "We encourage you to review this Policy periodically. Continued use of our website or services after changes become effective constitutes acceptance of the revised Policy.",
      ],
    },
    {
      title: "Version History",
      paragraphs: ["v1.0 — June 12, 2026 — Initial publication"],
    },
  ];

  const renderParagraph = (text: string, key: string) => {
    if (text.includes(privacyEmail)) {
      const parts = text.split(privacyEmail);
      return (
        <p key={key}>
          {parts.map((part, index) => (
            <React.Fragment key={`${key}-${index}`}>
              {part}
              {index < parts.length - 1 && <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>}
            </React.Fragment>
          ))}
        </p>
      );
    }

    if (text.includes("herculesautomated.com/contact")) {
      const parts = text.split("herculesautomated.com/contact");
      return (
        <p key={key}>
          {parts[0]}
          <a href="/contact">herculesautomated.com/contact</a>
          {parts[1]}
        </p>
      );
    }

    if (text.includes("allaboutcookies.org")) {
      const parts = text.split("allaboutcookies.org");
      return (
        <p key={key}>
          {parts[0]}
          <a href="https://allaboutcookies.org" target="_blank" rel="noopener noreferrer">
            allaboutcookies.org
          </a>
          {parts[1]}
        </p>
      );
    }

    return <p key={key}>{text}</p>;
  };

  const renderItems = (items: string[] | undefined, keyPrefix: string) =>
    items ? (
      <ul className="privacy-bullets">
        {items.map((item, index) => (
          <li key={`${keyPrefix}-item-${index}`}>{item}</li>
        ))}
      </ul>
    ) : null;

  return (
    <section className="privacy-section" aria-labelledby="privacy-heading">
      <div className="container privacy-container">
        <p className="section-label">Privacy</p>
        <h1 id="privacy-heading" className="page-h1">
          Privacy Policy
        </h1>
        <p className="privacy-company">Hercules Automation LLC</p>
        <p className="privacy-updated">Effective Date: June 12, 2026 | Last Revised: June 12, 2026</p>

        <div className="privacy-list">
          {policySections.map((section) => (
            <article className="privacy-item" key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph, index) => renderParagraph(paragraph, `${section.title}-${index}`))}
              {renderItems(section.items, section.title)}
              {section.afterItems?.map((paragraph, index) =>
                renderParagraph(paragraph, `${section.title}-after-${index}`)
              )}
              {section.subsections?.map((subsection, subsectionIndex) => (
                <div className="privacy-subsection" key={`${section.title}-${subsection.title}-${subsectionIndex}`}>
                  {subsection.title && <h3>{subsection.title}</h3>}
                  {subsection.paragraphs?.map((paragraph, index) =>
                    renderParagraph(paragraph, `${section.title}-${subsection.title}-${index}`)
                  )}
                  {renderItems(subsection.items, `${section.title}-${subsection.title}`)}
                  {subsection.afterItems?.map((paragraph, index) =>
                    renderParagraph(paragraph, `${section.title}-${subsection.title}-after-${index}`)
                  )}
                </div>
              ))}
            </article>
          ))}
        </div>

        <div className="privacy-contact">
          <h2>Questions?</h2>
          <p>
            Contact us at <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>.
          </p>
        </div>
      </div>
    </section>
  );
}

function NotFoundSection() {
  return (
    <section className="contact-section" aria-labelledby="not-found-heading">
      <div className="container contact-header">
        <p className="section-label">Not Found</p>
        <h1 id="not-found-heading" className="page-h1">
          404 Page Not Found
        </h1>
        <p className="cta-body">
          This page is not available. Visit the homepage or explore our AI automation agency services for small
          business operations.
        </p>
        <div className="hero-actions center-actions">
          <a href="/" className="btn-primary btn-lg">
            Go Home
          </a>
          <a href="/services" className="btn-outline btn-lg">
            View Services
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <a href="/" className="footer-logo" aria-label="Hercules Automation home">
            <span className="logo-mark" aria-hidden="true">
              <img
                src="/hercules_mark.webp"
                alt=""
                className="logo-img"
                width="598"
                height="681"
                loading="lazy"
                draggable={false}
              />
            </span>
            <span className="logo-text">
              <span>Hercules&nbsp;</span>
              <span className="gold-part">Automation</span>
            </span>
          </a>
          <p className="footer-tagline">Strength for business operations.</p>
        </div>

        <nav className="footer-nav" aria-label="Footer navigation">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
          <a href="/privacy-policy">Privacy Policy</a>
        </nav>

        <div className="footer-contact">
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
      </div>

      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Hercules Automation. All rights reserved.</p>
      </div>
    </footer>
  );
}

function HomePage() {
  return (
    <>
      <Hero />
      <SolutionSection />
      <ServicesSection />
      <HowItWorksSection />
      <ResultsSection />
      <AboutSection />
      <BlogSection />
      <FaqSection />
      <ContactSection />
    </>
  );
}

function RoutePage({ route }: { route: RouteKey }) {
  if (route === "/services") {
    return (
      <>
        <ServicesSection standalone />
        <ContactSection />
      </>
    );
  }

  if (route === "/case-studies") {
    return (
      <>
        <ResultsSection standalone />
        <ContactSection />
      </>
    );
  }

  if (route === "/about") {
    return (
      <>
        <AboutSection standalone />
        <ContactSection />
      </>
    );
  }

  if (route === "/contact") {
    return <ContactSection standalone />;
  }

  if (route === "/blog") {
    return (
      <>
        <BlogSection standalone />
        <ContactSection />
      </>
    );
  }

  if (route === "/privacy-policy") {
    return <PrivacyPolicySection />;
  }

  if (route === "/404.html") {
    return <NotFoundSection />;
  }

  return <HomePage />;
}

export default function AutomationAgency({
  initialRoute,
  renderSeo,
}: {
  initialRoute?: RouteKey;
  renderSeo?: boolean;
}) {
  const [route] = useState<RouteKey>(() => initialRoute || getRouteKey());
  const [shouldRenderSeo] = useState(
    () => renderSeo ?? (typeof document === "undefined" || !document.getElementById("structured-data"))
  );

  return (
    <div className="app">
      {shouldRenderSeo && <Seo route={route} />}
      <Header route={route} />
      <main>
        <RoutePage route={route} />
      </main>
      <Footer />
    </div>
  );
}
